import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  svgContainer: {
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    pointerEvents: 'none',
    backgroundColor: 'rgba(17, 24, 39, 0.92)',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.6',
    whiteSpace: 'nowrap',
    zIndex: 10,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  tooltipLabel: {
    fontWeight: 600,
    marginBottom: '4px',
    fontSize: '12px',
  },
  tooltipRow: {
    fontSize: '11px',
    color: '#d1d5db',
  },
  tooltipTrendUp: {
    color: '#4ade80',
    fontWeight: 600,
  },
  tooltipTrendDown: {
    color: '#f87171',
    fontWeight: 600,
  },
  tooltipTrendStable: {
    color: '#9ca3af',
    fontWeight: 600,
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  fieldSelector: {
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    backgroundColor: '#f9fafb',
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
  },
  legendRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginTop: '12px',
    fontSize: '11px',
    color: '#6b7280',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  legendSwatch: {
    width: '14px',
    height: '10px',
    borderRadius: '2px',
    display: 'inline-block',
  },
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  initialServiceVisit: 'Initial Service Visit',
  oneTime: 'One Time',
  mosquitoTickControl: 'Mosquito & Tick Control',
};

export default function PriceTrendRibbons({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  const activeData = useMemo(() => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
  const emptyStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '320px',
    background: '#f9fafb',
    border: '1px dashed #e5e7eb',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '14px',
    fontStyle: 'italic'
  };

  return <div style={emptyStyle}>No analytical data available for this selection.</div>;
}
    if (Array.isArray(data)) {
      if (!data || (Array.isArray(data) && data.length === 0)) {
  const emptyStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '320px',
    background: '#f9fafb',
    border: '1px dashed #e5e7eb',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '14px',
    fontStyle: 'italic'
  };

  return <div style={emptyStyle}>No analytical data available for this selection.</div>;
}
      const field = selectedField || data[0].field;
      return data.find((d) => d.field === field) || data[0];
    }
    return data;
  }, [data, selectedField]);

  const fieldOptions = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data.map((d) => d.field);
    return [data.field];
  }, [data]);

  useEffect(() => {
    if (fieldOptions.length > 0 && !selectedField) {
      setSelectedField(fieldOptions[0]);
    }
  }, [fieldOptions, selectedField]);

  useEffect(() => {
    if (!activeData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { dates, mean, sigma1Upper, sigma1Lower, sigma2Upper, sigma2Lower, trendDirection } =
      activeData;

    if (!dates || dates.length === 0) return;

    const width = 700;
    const height = 350;
    const margin = { top: 20, right: 30, bottom: 60, left: 55 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const parseDate = d3.timeParse('%Y-%m-%d');
    const parsedDates = dates.map((d) => parseDate(d));

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerWidth]);

    const allValues = [
      ...sigma2Upper,
      ...sigma2Lower,
      ...sigma1Upper,
      ...sigma1Lower,
      ...mean,
    ].filter((v) => v != null && !isNaN(v));

    const yMin = d3.min(allValues);
    const yMax = d3.max(allValues);
    const yPadding = (yMax - yMin) * 0.1 || 1;

    const yScale = d3
      .scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .range([innerHeight, 0])
      .nice();

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Background trend direction segments
    for (let i = 0; i < parsedDates.length - 1; i++) {
      const dir = trendDirection[i];
      if (dir === 'stable') continue;

      const x0 = xScale(parsedDates[i]);
      const x1 = xScale(parsedDates[i + 1]);

      g.append('rect')
        .attr('x', x0)
        .attr('y', 0)
        .attr('width', x1 - x0)
        .attr('height', innerHeight)
        .attr('fill', dir === 'up' ? '#dcfce7' : '#fee2e2')
        .attr('opacity', 0.5);
    }

    // +-2 sigma band (outer ribbon)
    const area2 = d3
      .area()
      .x((_, i) => xScale(parsedDates[i]))
      .y0((_, i) => yScale(sigma2Lower[i]))
      .y1((_, i) => yScale(sigma2Upper[i]))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(mean)
      .attr('d', area2)
      .attr('fill', '#93c5fd')
      .attr('opacity', 0.2);

    // +-1 sigma band (inner ribbon)
    const area1 = d3
      .area()
      .x((_, i) => xScale(parsedDates[i]))
      .y0((_, i) => yScale(sigma1Lower[i]))
      .y1((_, i) => yScale(sigma1Upper[i]))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(mean)
      .attr('d', area1)
      .attr('fill', '#60a5fa')
      .attr('opacity', 0.3);

    // Mean line
    const line = d3
      .line()
      .x((_, i) => xScale(parsedDates[i]))
      .y((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(mean)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2);

    // X-axis
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(Math.min(dates.length, 8))
      .tickFormat(d3.timeFormat('%b %d'));

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .selectAll('text')
      .attr('transform', 'rotate(-35)')
      .style('text-anchor', 'end')
      .style('font-size', '10px')
      .style('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#d1d5db');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y-axis
    const yAxis = d3
      .axisLeft(yScale)
      .ticks(6)
      .tickFormat((d) => `$${d.toFixed(0)}`);

    g.append('g')
      .call(yAxis)
      .selectAll('text')
      .style('font-size', '10px')
      .style('fill', '#6b7280');

    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(yScale.ticks(6))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-dasharray', '3,3');

    // Crosshair group (hidden initially)
    const crosshair = g.append('g').style('display', 'none');

    crosshair
      .append('line')
      .attr('class', 'crosshair-v')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    crosshair
      .append('line')
      .attr('class', 'crosshair-h')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    crosshair
      .append('circle')
      .attr('r', 4)
      .attr('fill', '#2563eb')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);

    // Hover overlay
    const bisect = d3.bisector((d) => d).left;

    svg
      .append('rect')
      .attr('transform', `translate(${margin.left},${margin.top})`)
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mousemove', function (event) {
        const [mx] = d3.pointer(event, this);
        const x0 = xScale.invert(mx);
        const idx = bisect(parsedDates, x0, 1);
        const d0 = parsedDates[idx - 1];
        const d1 = parsedDates[idx];
        if (!d0 && !d1) return;

        let i;
        if (!d1) {
          i = idx - 1;
        } else if (!d0) {
          i = idx;
        } else {
          i = x0 - d0 > d1 - x0 ? idx : idx - 1;
        }
        if (i < 0) i = 0;
        if (i >= parsedDates.length) i = parsedDates.length - 1;

        const cx = xScale(parsedDates[i]);
        const cy = yScale(mean[i]);

        crosshair.style('display', null);
        crosshair.select('.crosshair-v').attr('x1', cx).attr('x2', cx);
        crosshair.select('.crosshair-h').attr('y1', cy).attr('y2', cy);
        crosshair.select('circle').attr('cx', cx).attr('cy', cy);

        const containerRect = containerRef.current?.getBoundingClientRect();
        const svgRect = svgRef.current?.getBoundingClientRect();
        if (!containerRect || !svgRect) return;

        const svgScaleX = svgRect.width / width;
        const svgScaleY = svgRect.height / height;
        const tooltipX = margin.left * svgScaleX + cx * svgScaleX;
        const tooltipY = margin.top * svgScaleY + cy * svgScaleY;

        setTooltip({
          x: tooltipX + 16,
          y: tooltipY - 10,
          date: dates[i],
          mean: mean[i],
          s1Upper: sigma1Upper[i],
          s1Lower: sigma1Lower[i],
          s2Upper: sigma2Upper[i],
          s2Lower: sigma2Lower[i],
          trend: trendDirection[i],
        });
      })
      .on('mouseleave', function () {
        crosshair.style('display', 'none');
        setTooltip(null);
      });
  }, [activeData]);

  if (!data || (Array.isArray(data) && data.length === 0)) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Trend Confidence Bands</h3>
        <p style={styles.subtitle}>Mean price +/- 1 sigma/2 sigma with trend direction</p>
        <div style={styles.empty}>No trend data available.</div>
      </div>
    );
  }

  if (
    activeData &&
    (!activeData.dates || activeData.dates.length === 0)
  ) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Trend Confidence Bands</h3>
        <p style={styles.subtitle}>Mean price +/- 1 sigma/2 sigma with trend direction</p>
        <div style={styles.empty}>No trend data available for selected field.</div>
      </div>
    );
  }

  const trendStyle = (dir) => {
    if (dir === 'up') return styles.tooltipTrendUp;
    if (dir === 'down') return styles.tooltipTrendDown;
    return styles.tooltipTrendStable;
  };

  const trendLabel = (dir) => {
    if (dir === 'up') return 'Upward';
    if (dir === 'down') return 'Downward';
    return 'Stable';
  };

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div>
          <h3 style={styles.title}>Price Trend Confidence Bands</h3>
          <p style={styles.subtitle}>Mean price &plusmn; 1&sigma;/2&sigma; with trend direction</p>
        </div>
        {fieldOptions.length > 1 && (
          <select
            style={styles.fieldSelector}
            value={selectedField || ''}
            onChange={(e) => setSelectedField(e.target.value)}
          >
            {fieldOptions.map((f) => (
              <option key={f} value={f}>
                {FIELD_LABELS[f] || f}
              </option>
            ))}
          </select>
        )}
      </div>
      <div ref={containerRef} style={styles.svgContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 350"
          width="100%"
          preserveAspectRatio="xMidYMid meet"
        />
        {tooltip && (
          <div
            style={{
              ...styles.tooltip,
              left: tooltip.x,
              top: tooltip.y,
            }}
          >
            <div style={styles.tooltipLabel}>{tooltip.date}</div>
            <div style={styles.tooltipRow}>
              Mean: <strong style={{ color: '#f9fafb' }}>${tooltip.mean.toFixed(2)}</strong>
            </div>
            <div style={styles.tooltipRow}>
              &plusmn;1&sigma;: ${tooltip.s1Lower.toFixed(2)} &ndash; ${tooltip.s1Upper.toFixed(2)}
            </div>
            <div style={styles.tooltipRow}>
              &plusmn;2&sigma;: ${tooltip.s2Lower.toFixed(2)} &ndash; ${tooltip.s2Upper.toFixed(2)}
            </div>
            <div style={styles.tooltipRow}>
              Trend: <span style={trendStyle(tooltip.trend)}>{trendLabel(tooltip.trend)}</span>
            </div>
          </div>
        )}
      </div>
      <div style={styles.legendRow}>
        <span style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, backgroundColor: '#2563eb' }} />
          Mean
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, backgroundColor: '#60a5fa', opacity: 0.5 }} />
          &plusmn;1&sigma;
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, backgroundColor: '#93c5fd', opacity: 0.35 }} />
          &plusmn;2&sigma;
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, backgroundColor: '#dcfce7' }} />
          Uptrend
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, backgroundColor: '#fee2e2' }} />
          Downtrend
        </span>
      </div>
    </div>
  );
}
